import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "keyloader-title" }
const _hoisted_2 = {
  key: 0,
  class: "keyloader-info"
}
const _hoisted_3 = {
  key: 1,
  class: "keyloader-error"
}

import { ref } from 'vue'
import { useDisplay } from 'vuetify'
import { useTabsStore } from '@/stores/tabs'
import type { TreeNode } from '@/types/editor'


export default /*@__PURE__*/_defineComponent({
  __name: 'KeyLoader',
  props: {
    activeNode: {}
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const { mobile: isMobile } = useDisplay()
const store = useTabsStore()

const props = __props

const emit = __emit

// ─── State ────────────────────────────────────────────────────────────────────

const keyFileModel = ref<File | File[] | null>(null)
const keyBytes = ref<Uint8Array | null>(null)
const keyReady = ref(false)
const hexPreview = ref('')
const errorMsg = ref('')
const infoMsg = ref('')

// ─── Helpers ──────────────────────────────────────────────────────────────────

function dec2hex(i: number): string {
  return (i + 0x10000).toString(16).substr(-2).toUpperCase()
}

function normalizeLabel(s: unknown): string {
  return String(s ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

function base64ToUint8Array(base64: string): Uint8Array {
  const bin = atob(base64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return bytes
}

function readFileAsBytes(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer))
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(file)
  })
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(file)
  })
}

// ─── Core methods ─────────────────────────────────────────────────────────────

async function onKeyFileChanged(fileOrArr: File | File[] | null): Promise<void> {
  errorMsg.value = ''
  infoMsg.value = ''
  hexPreview.value = ''
  keyBytes.value = null
  keyReady.value = false

  const file = Array.isArray(fileOrArr) ? fileOrArr[0] : fileOrArr
  if (!file) return

  try {
    const rawBytes = await readFileAsBytes(file)

    let text: string | null = null
    try {
      text = await readFileAsText(file)
    } catch {
      text = null
    }

    if (text && text.includes('-----BEGIN')) {
      const base64Body = text
        .replace(/-----BEGIN [^-]+-----/g, '')
        .replace(/-----END [^-]+-----/g, '')
        .replace(/\s+/g, '')
        .trim()

      if (!base64Body) {
        errorMsg.value = 'Could not read PEM body (base64 is empty).'
        return
      }
      keyBytes.value = base64ToUint8Array(base64Body)
    } else {
      keyBytes.value = rawBytes
    }

    keyReady.value = true
    const previewLen = Math.min(96, keyBytes.value.length)
    const head = Array.from(keyBytes.value.slice(0, previewLen)).map(dec2hex).join(' ')
    hexPreview.value = head + (keyBytes.value.length > previewLen ? ' ...' : '')
    infoMsg.value = `Loaded ${keyBytes.value.length} bytes.`
  } catch (e: any) {
    console.error('Key load error:', e)
    errorMsg.value = 'Failed to load key: ' + (e.message ?? 'Unknown error')
  }
}

function insertIntoSelectedNode(): void {
  errorMsg.value = ''
  infoMsg.value = ''
  if (!keyReady.value || !keyBytes.value) return

  const highlightedId = store.highlighted
  const targetId =
    highlightedId !== null && highlightedId !== undefined && highlightedId !== -1
      ? highlightedId
      : props.activeNode?.id ?? null

  if (targetId === null) {
    errorMsg.value =
      'Select a node first (click a byte on the right, or right-click a node in the tree).'
    return
  }

  if (applyBytesToNode(targetId, keyBytes.value)) {
    infoMsg.value = `Inserted into node ${targetId}.`
  }
}

function autoInsertIntoSubjectPublicKeyInfo(): void {
  errorMsg.value = ''
  infoMsg.value = ''
  if (!keyReady.value || !keyBytes.value) return

  const node = store.tree.find(n => normalizeLabel(n.label).includes('subjectpublickeyinfo'))

  if (!node) {
    errorMsg.value =
      'No node label matches "subjectPublicKeyInfo". (Try INSERT INTO SELECTED NODE for TLS_EXAMPLE.)'
    return
  }

  if (applyBytesToNode(node.id, keyBytes.value)) {
    infoMsg.value = `Inserted into ${node.label || node.id}.`
  }
}

function autoInsertIntoSubjectPublicKey(): void {
  errorMsg.value = ''
  infoMsg.value = ''
  if (!keyReady.value || !keyBytes.value) return

  const node = store.tree.find(n => normalizeLabel(n.label).includes('subjectpublickey'))

  if (!node) {
    errorMsg.value = 'No node label matches "subjectPublicKey".'
    return
  }

  const out = new Uint8Array(keyBytes.value.length + 1)
  out[0] = 0x00
  out.set(keyBytes.value, 1)

  if (applyBytesToNode(node.id, out)) {
    infoMsg.value = `Inserted into ${node.label || node.id}.`
  }
}

function clearKey(): void {
  keyFileModel.value = null
  keyBytes.value = null
  keyReady.value = false
  hexPreview.value = ''
  errorMsg.value = ''
  infoMsg.value = ''
}

function applyBytesToNode(nodeId: number, bytesUint8: Uint8Array): boolean {
  const node = store.getNodeFromId(nodeId)
  if (!node || node.id === -1) {
    errorMsg.value = `Node not found: ${nodeId}`
    return false
  }

  const bytesArray = Array.from(bytesUint8)

  try {
    store.nodeChanged({
      tab: store.currentTab,
      id: nodeId,
      tag: node.tag ? node.tag[0] : null,
      length: null,
      content: bytesArray as any
    })
    store.elementHighlighted(nodeId)
    return true
  } catch (e) {
    console.warn('store.nodeChanged failed:', e)
  }

  try {
    store.nodeUpdated({
      tab: store.currentTab,
      id: nodeId,
      value: bytesArray,
      field: 'content'
    })
    store.elementHighlighted(nodeId)
    return true
  } catch (e) {
    console.warn('store.nodeUpdated failed:', e)
  }

  errorMsg.value = 'Failed to insert key bytes into node.'
  return false
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_file_input = _resolveComponent("v-file-input")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!

  return (_openBlock(), _createBlock(_component_v_card, {
    class: "keyloader-card d-flex flex-column",
    width: _unref(isMobile) ? '95vw' : '80vw',
    height: _unref(isMobile) ? '90vh' : '78vh',
    elevation: "2"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card_text, { class: "keyloader-body" }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createVNode(_component_v_icon, {
              class: "keyloader-title-icon",
              style: {"color":"#1976D2"}
            }, {
              default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                _createTextVNode("mdi-key-variant", -1)
              ]))]),
              _: 1
            }),
            _cache[4] || (_cache[4] = _createElementVNode("div", { class: "keyloader-title-text" }, "Key Loader", -1))
          ]),
          _cache[12] || (_cache[12] = _createElementVNode("div", { class: "keyloader-desc" }, " Load a key file (PEM or DER) and insert its bytes into a selected ASN.1 node or automatically into a node by label. ", -1)),
          _createVNode(_component_v_file_input, {
            modelValue: keyFileModel.value,
            "onUpdate:modelValue": [
              _cache[0] || (_cache[0] = ($event: any) => ((keyFileModel).value = $event)),
              onKeyFileChanged
            ],
            class: "keyloader-input",
            label: "Load key file (.pem, .der, .key)",
            variant: "outlined",
            density: "comfortable",
            accept: ".pem,.der,.key,.cer,.crt,.bin",
            multiple: false,
            "hide-details": "",
            "prepend-inner-icon": "mdi-file-key-outline"
          }, null, 8, ["modelValue"]),
          _createVNode(_component_v_textarea, {
            modelValue: hexPreview.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((hexPreview).value = $event)),
            class: "keyloader-preview",
            label: "Key bytes preview (hex, first 96 bytes)",
            variant: "outlined",
            density: "comfortable",
            rows: "7",
            readonly: "",
            "hide-details": ""
          }, null, 8, ["modelValue"]),
          _createVNode(_component_v_divider, { class: "my-6" }),
          _createVNode(_component_v_row, { dense: "" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, {
                cols: "12",
                md: "6"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    class: "keyloader-btn",
                    variant: "outlined",
                    disabled: !keyReady.value,
                    block: "",
                    onClick: insertIntoSelectedNode
                  }, {
                    default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                      _createTextVNode(" INSERT INTO SELECTED NODE ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["disabled"]),
                  _cache[6] || (_cache[6] = _createElementVNode("div", { class: "keyloader-help" }, " Uses highlighted node if present, otherwise the last right-clicked node. ", -1))
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, {
                cols: "12",
                md: "6"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    class: "keyloader-btn",
                    variant: "outlined",
                    disabled: !keyReady.value,
                    block: "",
                    onClick: autoInsertIntoSubjectPublicKeyInfo
                  }, {
                    default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                      _createTextVNode(" AUTO-INSERT INTO SUBJECTPUBLICKEYINFO ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["disabled"]),
                  _cache[8] || (_cache[8] = _createElementVNode("div", { class: "keyloader-help" }, "Searches node labels and inserts into the first match.", -1))
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, {
                cols: "12",
                md: "6",
                class: "mt-2"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    class: "keyloader-btn",
                    variant: "outlined",
                    disabled: !keyReady.value,
                    block: "",
                    onClick: autoInsertIntoSubjectPublicKey
                  }, {
                    default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                      _createTextVNode(" AUTO-INSERT INTO SUBJECTPUBLICKEY ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["disabled"]),
                  _cache[10] || (_cache[10] = _createElementVNode("div", { class: "keyloader-help" }, "Useful if you want to overwrite a BIT STRING node.", -1))
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, {
                cols: "12",
                md: "6",
                class: "mt-2"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    class: "keyloader-btn",
                    variant: "outlined",
                    disabled: !keyReady.value && !keyFileModel.value,
                    block: "",
                    onClick: clearKey
                  }, {
                    default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                      _createTextVNode(" CLEAR LOADED KEY ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["disabled"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          (infoMsg.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(infoMsg.value), 1))
            : _createCommentVNode("", true),
          (errorMsg.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(errorMsg.value), 1))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createVNode(_component_v_card_actions, { class: "keyloader-actions" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_spacer),
          _createVNode(_component_v_btn, {
            class: "keyloader-close",
            variant: "tonal",
            style: {"color":"#1976D2"},
            onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('close')))
          }, {
            default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
              _createTextVNode("CLOSE", -1)
            ]))]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["width", "height"]))
}
}

})